import { useCallback, type FC } from 'react';
import { Modal } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '@/store/reduxHooks';
import { closeCallModal } from '@/store/calls';
import { useCallOrdersTabs } from '@/hooks/useCallOrdersTabs';
import { getCallSummary } from '@/utils/getCallSummary';
import { CallAccountCard } from './components/CallAccountCard/CallAccountCard';
import { CallOrdersTabs } from './components/CallOrdersTabs/CallOrdersTabs';
import styles from './callInfoModal.module.scss';

/**
 * Модальное окно звонка — одно на оба сценария (живой входящий и пропущенный
 * из истории). Живёт на уровне App и открывается только вручную, кликом по
 * уведомлению: гашение тоста модалку не закрывает.
 */
export const CallInfoModal: FC = () => {
  const dispatch = useAppDispatch();
  const activeCall = useAppSelector((state) => state.calls.activeCall);
  const isModalOpen = useAppSelector((state) => state.calls.isModalOpen);

  const {
    tabs,
    activeKind,
    activeOrders,
    activeEmptyText,
    showClient,
    accountId,
    changeTab
  } = useCallOrdersTabs({
    account: activeCall?.account ?? null,
    phone: activeCall?.phone ?? '',
    isModalOpen
  });

  const handleClose = useCallback((): void => {
    dispatch(closeCallModal());
  }, [dispatch]);

  if (activeCall === null) {
    return null;
  }

  const { title, subtitle, accountPath } = getCallSummary(activeCall);

  return (
    <Modal
      show={isModalOpen}
      onHide={handleClose}
      centered
      scrollable
      size="lg"
      className={styles['call-info-modal']}
    >
      <Modal.Header closeButton className={styles['call-info-modal__header']}>
        <Modal.Title className={styles['call-info-modal__title']}>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles['call-info-modal__body']}>
        <p className={styles['call-info-modal__subtitle']}>{subtitle}</p>

        <CallAccountCard
          account={activeCall.account}
          accountPath={accountPath}
        />

        <CallOrdersTabs
          tabs={tabs}
          activeKind={activeKind}
          data={activeOrders}
          emptyText={activeEmptyText}
          showClient={showClient}
          accountId={accountId}
          onTabChange={changeTab}
        />
      </Modal.Body>
    </Modal>
  );
};
